import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { BehaviorSubject, Observable } from 'rxjs'
import { tap } from 'rxjs/operators'
import { jwtDecode } from 'jwt-decode'
import { AuthenticationResult } from '@azure/msal-browser'

export interface MappingGroup {
  id: string
  name: string
  role: string
}

@Injectable({
  providedIn: 'root',
})
export class AzureService {

  // Admin group IDs (only for admin detection)
  private readonly ADMIN_GROUP_IDS = [
    'eba71cd2-0d6e-4136-9746-f8ffba5ec0c1',
    '19c79f09-bb8a-4b5e-bb5f-2fa8936dfb35'
  ];

  // Allowed role groups
  private readonly ALLOWED_ROLE_GROUP_IDS = [
    '4d26b2da-438a-4dc4-97e5-38d71c45ce90',
    'eba71cd2-0d6e-4136-9746-f8ffba5ec0c1',
    'b1d54686-7d00-4fa6-91ef-0711b4f52385',
    '19c79f09-bb8a-4b5e-bb5f-2fa8936dfb35',
    'aa66a4a1-14e9-49c8-a9e0-4ea386423565',
    '1fc3f32d-a3e3-469f-b82b-c35a3d774bb6',
    '8a408319-b955-44c4-966b-aff1377613d8'
  ];
  private currentUserSubject = new BehaviorSubject<any>(null)
  authServiceInstance: any
  token = new BehaviorSubject<any>(null)
  rolesSubject = new BehaviorSubject<any>([]);
  selectedRole = new BehaviorSubject<string>('')
  selectedBusinessArea = new BehaviorSubject<string>('')


  constructor(private http: HttpClient) {
  }

  getUserInfo(): Observable<any> {
    const url = 'https://graph.microsoft.com/v1.0/me'
    return this.http.get(url).pipe(
      tap((userData) => {
        this.currentUserSubject.next(userData)
      })
    )
  }

  getCurrentUser(): Observable<any> {
    return this.currentUserSubject.asObservable()
  }

  getCurrentUserValue(): any {
    return this.currentUserSubject.value
  }

  setAuthServiceInstance(authService: any) {
    this.authServiceInstance = authService
  }

  getAuthServiceInstance() {
    return this.authServiceInstance
  }

  async getAccessToken(): Promise<any> {
    if (!this.authServiceInstance) {
      throw new Error('Auth service instance is not set.')
    }

    try {
      const tokenResponse: AuthenticationResult = await this.authServiceInstance
        .acquireTokenSilent({
          scopes: [
            `${this.authServiceInstance.instance.controller.config.auth.clientId}/.default`,
          ],
        })
        .toPromise()

      if (tokenResponse) {
        const idTokenDecoded = jwtDecode(tokenResponse.idToken)
        return {
          accessToken: tokenResponse.accessToken,
          idToken: tokenResponse.idToken,
          idTokenDecoded: idTokenDecoded,
          groupIds: [...new Set((tokenResponse.idTokenClaims as any).groups)],
        }
      }
    } catch (error) {
      console.error('Error fetching Token:', error)
      throw error
    }
  }

  logout() {
    this.authServiceInstance.logoutRedirect({
      postLogoutRedirectUri: '/',
    })
    this.authServiceInstance = null
    this.currentUserSubject.next(null)
    localStorage.removeItem('role')
  }

async getRoles(): Promise<MappingGroup[]> {

  if (this.rolesSubject.value.length > 0) {
    console.log('[Roles] Returning cached roles:', this.rolesSubject.value);
    return this.rolesSubject.value;
  }

    try {
      let memberUrl = 'https://graph.microsoft.com/v1.0/me/memberOf';
      let memberGroups: any[] = [];

      while (memberUrl) {
        const response: any = await this.http.get(memberUrl).toPromise();
        const values = response?.value || [];
        memberGroups = [...memberGroups, ...values];
        console.log('[Roles] Page memberOf groups:', values.length);
        memberUrl = response['@odata.nextLink'] || null;
      }

      let ownedUrl = 'https://graph.microsoft.com/v1.0/me/ownedObjects';
      let ownedObjects: any[] = [];

      while (ownedUrl) {
        const response: any = await this.http.get(ownedUrl).toPromise();
        const values = response?.value || [];
        ownedObjects = [...ownedObjects, ...values];
        console.log('[Roles] Page ownedObjects:', values.length);
        ownedUrl = response['@odata.nextLink'] || null;
      }

      console.log('[Roles] Total memberOf:', memberGroups.length);
      console.log('[Roles] Total ownedObjects:', ownedObjects.length);

      const memberOfGroups = memberGroups.filter(
        (item: any) => item['@odata.type'] === '#microsoft.graph.group'
      );

      const ownedGroups = ownedObjects.filter(
        (item: any) => item['@odata.type'] === '#microsoft.graph.group'
      );


      const combinedGroups = [...memberOfGroups, ...ownedGroups];
      const uniqueGroups = Array.from(
        new Map(combinedGroups.map(g => [g.id, g])).values()
      );

      console.log('[Roles] Unique combined groups:', uniqueGroups.length);

  
      const roleFiltered = uniqueGroups.filter((group: any) =>
        this.ALLOWED_ROLE_GROUP_IDS.includes(group.id)
      );

      console.log('[Roles] After allowed role filter:', roleFiltered.length);

      const roles: MappingGroup[] = roleFiltered.map((group: any) => ({
        id: group.id,
        name: group.displayName,
        role: group.displayName,
      }));

      console.log('[Roles] Final roles:', roles);

      this.rolesSubject.next(roles);
      return roles;

    } catch (error) {
      console.error('[Roles] Error fetching roles from Microsoft Graph API:', error);
      this.rolesSubject.next([]);
      return [];
    }
  }

  getRolesObservable(): Observable<string> {
    return this.rolesSubject.asObservable();
  }

  isAdminRole(roleId: string): boolean {
    return this.ADMIN_GROUP_IDS.includes(roleId);
  }

}
